"use client";

import { useState } from "react";
import { Check, Copy } from "lucide-react";

import { cn } from "@/lib/utils";

function CopyButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label="Copy code"
      onClick={() => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </button>
  );
}

export function CodeBlock({
  code,
  title,
  className,
}: {
  code: string;
  title?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "mb-6 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 dark:border-zinc-800/80",
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-zinc-800/80 bg-zinc-900/60 py-1 pl-4 pr-2">
        <span className="font-mono text-xs text-zinc-400">{title ?? ""}</span>
        <CopyButton code={code} />
      </div>
      <pre className="overflow-x-auto p-4 text-[13px] leading-6 text-zinc-200">
        <code className="font-mono">{code}</code>
      </pre>
    </div>
  );
}

export interface CodeTab {
  label: string;
  code: string;
}

export function CodeTabs({ tabs }: { tabs: CodeTab[] }) {
  const [active, setActive] = useState(0);
  const current = tabs[active];

  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 dark:border-zinc-800/80">
      <div className="flex items-center justify-between border-b border-zinc-800/80 bg-zinc-900/60 pr-2">
        <div className="flex">
          {tabs.map((tab, index) => (
            <button
              key={tab.label}
              type="button"
              onClick={() => setActive(index)}
              className={cn(
                "border-b-2 px-4 py-2 font-mono text-xs transition-colors",
                index === active
                  ? "border-lime-300 text-zinc-100"
                  : "border-transparent text-zinc-500 hover:text-zinc-300",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <CopyButton code={current.code} />
      </div>
      <pre className="overflow-x-auto p-4 text-[13px] leading-6 text-zinc-200">
        <code className="font-mono">{current.code}</code>
      </pre>
    </div>
  );
}
